import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Box, Collapse, Grid, MenuItem, Typography } from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import { AppSnackbar, ConfigFieldControl, ConfigTextField } from '@sierralabs/core-ui';
import { SubTaskListCard, TaskEvent, TaskEventType, useTaskEntityData, useTaskFields } from '@sierralabs/workflow-ui';
import { useConfigComponent } from '../../hooks/component/useConfigComponent';
import { useSubscribeComponentChanges } from '../../hooks/component/useSubscribeComponentChanges';
import { useSiteContext } from '../../SiteContext';
import { SiteComponent } from '../SiteComponentPlugin';
import { SiteGridItemPlugin } from './SiteGridItemPlugin';
import { SiteTaskStatusCardConfigurator } from './SiteTaskStatusCardPlugin';
const StyledAlertTitle = styled(AlertTitle) `
  font-size: 15px;
  font-weight: 600;
`;
export const SiteSubTaskListCard = props => {
    const component = useSubscribeComponentChanges(SiteSubTaskListCardPlugin, props);
    const { editing, isHover, hoverProps } = useSiteContext();
    const classes = ['site-subtask-list-card'];
    if (isHover(component)) {
        classes.push('component-hover');
    }
    const className = classes.join(' ');
    const cProps = component.props || {};
    const { entityId, entityTable, routeParam, grid = { xs: 12 } } = cProps;
    const params = useParams();
    const dataId = routeParam ? params[routeParam] : undefined;
    const [error, setError] = useState();
    const { task, taskId, isLatest, findTask, setTask } = useTaskEntityData(entityId || entityTable || '', dataId);
    const { isFieldDisabled } = useTaskFields(task, false);
    useEffect(() => {
        // refresh on change status
        return TaskEvent.handle(TaskEventType.ChangeStatus, () => taskId && findTask());
    }, [taskId, findTask]);
    // render wrapped with grid item
    const render = (children) => {
        return (React.createElement(Grid, Object.assign({ item: true, xs: grid.xs, sm: grid.sm, md: grid.md, lg: grid.lg, className: className }, hoverProps(component)), children));
    };
    return (React.createElement(SiteComponent, Object.assign({ plugin: SiteSubTaskListCardPlugin }, props),
        editing &&
            !task &&
            render(React.createElement(Alert, { severity: 'info' },
                React.createElement(StyledAlertTitle, null, "Site Edit Mode"),
                "SubTask List Card is displayed here.")),
        (task === null || task === void 0 ? void 0 : task.children) &&
            task.children.length > 0 &&
            render(React.createElement(SubTaskListCard, { task: task, title: cProps.title, dialogTitle: cProps.dialogTitle, dialogMessage: cProps.dialogMessage, disabled: !isLatest || isFieldDisabled('assignSubTasks'), onError: setError, onChange: updatedTask => {
                    setTask(task => {
                        const newTask = Object.assign({}, task, updatedTask);
                        // refresh task
                        TaskEvent.dispatch(TaskEventType.ChangeStatus, {
                            task: newTask
                        });
                        return newTask;
                    });
                } })),
        React.createElement(AppSnackbar, { severity: 'error', open: !!error, onClose: () => setError(undefined) }, (error === null || error === void 0 ? void 0 : error.message) || 'An error occurred.')));
};
const StyledAlert = styled(Alert) `
  margin-bottom: 8px;
  .MuiAlertTitle-root {
    font-size: 15px;
    font-weight: 600;
  }
`;
export const SiteSubTaskListCardConfigurator = props => {
    var _a, _b, _c, _d;
    const { component, update } = useConfigComponent(props.component);
    // show alert to make sure this component is not under a Grid Item component
    const hasGridItemParent = ((_a = component.parent) === null || _a === void 0 ? void 0 : _a.type) === SiteGridItemPlugin.type;
    return (React.createElement(React.Fragment, null,
        React.createElement(Collapse, { in: hasGridItemParent, style: { minHeight: 'unset' } },
            React.createElement(StyledAlert, { severity: 'warning' },
                React.createElement(AlertTitle, null,
                    "Parent is a ",
                    SiteGridItemPlugin.name,
                    " component"),
                React.createElement(Box, { mb: 1 },
                    "This component should ",
                    React.createElement("strong", null, "not"),
                    " be under a",
                    ' ',
                    React.createElement("strong", null, SiteGridItemPlugin.name),
                    " component to avoid styling issues when hiding and showing the card."),
                React.createElement(Box, null,
                    React.createElement("strong", null, SiteSubTaskListCardPlugin.name),
                    " component already uses and contains Grid configuration."))),
        React.createElement(ConfigFieldControl, null,
            React.createElement(ConfigTextField, { fullWidth: true, label: 'Title', value: ((_b = component.props) === null || _b === void 0 ? void 0 : _b.title) || '', onChange: event => update('props.title', event.target.value) })),
        React.createElement(ConfigFieldControl, null,
            React.createElement(ConfigTextField, { fullWidth: true, label: 'Dialog Title', value: ((_c = component.props) === null || _c === void 0 ? void 0 : _c.dialogTitle) || '', onChange: event => update('props.dialogTitle', event.target.value) })),
        React.createElement(ConfigFieldControl, null,
            React.createElement(ConfigTextField, { fullWidth: true, label: 'Dialog Message', value: ((_d = component.props) === null || _d === void 0 ? void 0 : _d.dialogMessage) || '', onChange: event => update('props.dialogMessage', event.target.value) })),
        React.createElement(SiteTaskStatusCardConfigurator, Object.assign({}, props)),
        React.createElement(Box, { mt: 1 },
            React.createElement(Typography, { variant: 'overline' }, "Grid")),
        ['xs', 'sm', 'md', 'lg'].map(size => {
            var _a, _b;
            return (React.createElement(ConfigFieldControl, { key: size },
                React.createElement(ConfigTextField, { fullWidth: true, type: 'number', label: size.toUpperCase(), value: ((_b = (_a = component.props) === null || _a === void 0 ? void 0 : _a.grid) === null || _b === void 0 ? void 0 : _b[size]) || '', onChange: event => {
                        var _a;
                        if (!((_a = component.props) === null || _a === void 0 ? void 0 : _a.grid)) {
                            update('props.grid', {});
                        }
                        const value = event.target.value;
                        update(`props.grid.${size}`, value === 'auto' ? 'auto' : parseInt(value));
                    } }, ['auto', 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(spacing => (React.createElement(MenuItem, { key: spacing, value: spacing }, spacing))))));
        })));
};
export const SiteSubTaskListCardPlugin = {
    name: 'SubTask List Card',
    type: 'site-subtask-list-card',
    component: SiteSubTaskListCard,
    configurator: SiteSubTaskListCardConfigurator,
    initConfig: component => {
        if (!component.props)
            component.props = {};
        component.props.title = 'Stakeholders';
        component.props.dialogTitle = 'Assign Reviewers';
        component.props.dialogMessage = 'Select Users to assign';
        component.props.grid = { xs: 12 };
        return component;
    }
};
//# sourceMappingURL=SiteSubTaskListCardPlugin.js.map